import { FrontendApi, Session, Configuration } from '@ory/client-fetch';
import type {
  CreateBrowserLoginFlowRequest,
  GetLoginFlowRequest,
  CreateBrowserLogoutFlowRequest,
  LogoutFlow,
  LoginFlow,
  RegistrationFlow,
  SettingsFlow,
  RecoveryFlow,
  VerificationFlow,
  CreateBrowserRegistrationFlowRequest,
  UpdateRegistrationFlowRequest,
  SuccessfulNativeRegistration,
  GetRegistrationFlowRequest,
  UpdateLoginFlowRequest,
  SuccessfulNativeLogin,
  GetVerificationFlowRequest,
  UpdateVerificationFlowRequest,
  CreateBrowserRecoveryFlowRequest,
  GetRecoveryFlowRequest,
  UpdateRecoveryFlowRequest,
  UpdateLogoutFlowRequest,
  ToSessionRequest,
  CreateBrowserVerificationFlowRequest,
  CreateBrowserSettingsFlowRequest,
  GetSettingsFlowRequest,
  UpdateSettingsFlowRequest,
} from '@ory/client-fetch';
import { ref } from 'vue';

const config = new Configuration({
  // basePath: `https://${import.meta.env.VITE_ORY_SLUG}.projects.oryapis.com`,
  basePath: 'http://localhost:4000',
  credentials: 'include',
  headers: {
    Accept: 'application/json',
  },
});

const myOry = new FrontendApi(config);

export type KratosFlow = LoginFlow | RegistrationFlow | SettingsFlow | RecoveryFlow | VerificationFlow | LogoutFlow;

export function useOryAuth(): {
  session: ReturnType<typeof ref<Session | null>>;
  sessionToJWT: (params: ToSessionRequest) => Promise<Session | null>;
  currentFlow: ReturnType<typeof ref<KratosFlow | null>>;
  checkSession: () => Promise<Session | null>;
  createRegistrationFlow: (params: CreateBrowserRegistrationFlowRequest) => Promise<RegistrationFlow>;
  getRegistrationFlow: (params: GetRegistrationFlowRequest) => Promise<RegistrationFlow>;
  createLogoutFlow: (request?: CreateBrowserLogoutFlowRequest) => Promise<LogoutFlow>;
  updateLogoutFlow: (params: UpdateLogoutFlowRequest) => Promise<void>;
  getLoginFlow: (params: GetLoginFlowRequest) => Promise<LoginFlow>;
  createLoginFlow: (params: CreateBrowserLoginFlowRequest) => Promise<LoginFlow>;
  updateRegistrationFlow: (params: UpdateRegistrationFlowRequest) => Promise<SuccessfulNativeRegistration>;
  createVerificationFlow: (params: CreateBrowserVerificationFlowRequest) => Promise<VerificationFlow>;
  updateLoginFlow: (params: UpdateLoginFlowRequest) => Promise<SuccessfulNativeLogin>;
  getVerificationFlow: (params: GetVerificationFlowRequest) => Promise<VerificationFlow>;
  updateVerificationFlow: (params: UpdateVerificationFlowRequest) => Promise<VerificationFlow>;
  createBrowserRecoveryFlow: (params: CreateBrowserRecoveryFlowRequest) => Promise<RecoveryFlow>;
  getRecoveryFlow: (params: GetRecoveryFlowRequest) => Promise<RecoveryFlow>;
  updateRecoveryFlow: (params: UpdateRecoveryFlowRequest) => Promise<RecoveryFlow>;
  createBrowserSettingFlow: (params?: CreateBrowserSettingsFlowRequest) => Promise<SettingsFlow>;
  getSettingFlow: (params: GetSettingsFlowRequest) => Promise<SettingsFlow>;
  updateSettingFlow: (params: UpdateSettingsFlowRequest) => Promise<SettingsFlow>;
} {
  const session = ref<Session | null>(null);
  const currentFlow = ref<KratosFlow | null>(null);

  const checkSession = async (): Promise<Session | null> => {
    try {
      const response = await myOry.toSession();
      session.value = response.active ? response : null;
      return session.value;
    } catch (error) {
      const response = (error as { response?: Response }).response;

      if (response?.status === 401) {
        session.value = null;
        return null;
      }

      if (response?.status === 403) {
        const data = await response
          .clone()
          .json()
          .catch(() => null);
        if (data?.error?.id === 'session_aal2_required') {
          session.value = null;
          throw new OryAal2RequiredError();
        }
      }

      return handleApiError(error);
    }
  };

  const sessionToJWT = async (params: ToSessionRequest): Promise<Session | null> => {
    try {
      const response = await myOry.toSession({
        ...params,
        tokenizeAs: params?.tokenizeAs || 'jwt_template',
      });
      if (response && response.active) {
        return response;
      }
      return null;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const createRegistrationFlow = async (
    params: CreateBrowserRegistrationFlowRequest = {},
  ): Promise<RegistrationFlow> => {
    try {
      const response = await myOry.createBrowserRegistrationFlow(params);
      currentFlow.value = response;
      return response;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const updateRegistrationFlow = async (
    params: UpdateRegistrationFlowRequest,
  ): Promise<SuccessfulNativeRegistration> => {
    try {
      const response = await myOry.updateRegistrationFlow(params);
      return response;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const getRegistrationFlow = async (params: GetRegistrationFlowRequest): Promise<RegistrationFlow> => {
    const flowId = params.id;
    if (!flowId) {
      throw new Error('Flow ID is required');
    }

    try {
      const flow = await myOry.getRegistrationFlow({ ...params, id: flowId });
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const createLoginFlow = async (params: CreateBrowserLoginFlowRequest): Promise<LoginFlow> => {
    try {
      const flow = await myOry.createBrowserLoginFlow(params);
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const getLoginFlow = async (params: GetLoginFlowRequest): Promise<LoginFlow> => {
    const flowId = params.id;
    if (!flowId) {
      throw new Error('Flow ID is required');
    }

    try {
      const flow = await myOry.getLoginFlow({ ...params, id: flowId });
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const updateLoginFlow = async (params: UpdateLoginFlowRequest): Promise<SuccessfulNativeLogin> => {
    const flowId = params.flow;
    if (!flowId) {
      throw new Error('Flow ID is required');
    }

    try {
      const response = await myOry.updateLoginFlow({ ...params, flow: flowId });
      return response;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const createLogoutFlow = async (request?: CreateBrowserLogoutFlowRequest): Promise<LogoutFlow> => {
    try {
      const flow = await myOry.createBrowserLogoutFlow(request);
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const updateLogoutFlow = async (params: UpdateLogoutFlowRequest): Promise<void> => {
    const token = params.token;
    if (!token) {
      throw new Error('Token is required');
    }

    try {
      return await myOry.updateLogoutFlow({ ...params, token });
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const createVerificationFlow = async (params: CreateBrowserVerificationFlowRequest): Promise<VerificationFlow> => {
    try {
      const flow = await myOry.createBrowserVerificationFlow(params);
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const getVerificationFlow = async (params: GetVerificationFlowRequest): Promise<VerificationFlow> => {
    const flowId = params.id;
    if (!flowId) {
      throw new Error('Flow ID is required');
    }
    try {
      const flow = await myOry.getVerificationFlow({ ...params, id: flowId });
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const updateVerificationFlow = async (params: UpdateVerificationFlowRequest): Promise<VerificationFlow> => {
    const flowId = params.flow;
    if (!flowId) {
      throw new Error('Flow ID is required');
    }

    try {
      const flow = await myOry.updateVerificationFlow({ ...params, flow: flowId });
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const createBrowserRecoveryFlow = async (params: CreateBrowserRecoveryFlowRequest): Promise<RecoveryFlow> => {
    try {
      const flow = await myOry.createBrowserRecoveryFlow(params);
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const getRecoveryFlow = async (params: GetRecoveryFlowRequest): Promise<RecoveryFlow> => {
    const flowId = params.id;
    if (!flowId) {
      throw new Error('Flow ID is required');
    }

    try {
      const flow = await myOry.getRecoveryFlow({ ...params, id: flowId });
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const updateRecoveryFlow = async (params: UpdateRecoveryFlowRequest): Promise<RecoveryFlow> => {
    const flowId = params.flow;
    if (!flowId) {
      throw new Error('Flow ID is required');
    }

    try {
      const flow = await myOry.updateRecoveryFlow({ ...params, flow: flowId });
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const createBrowserSettingFlow = async (params?: CreateBrowserSettingsFlowRequest): Promise<SettingsFlow> => {
    try {
      const flow = await myOry.createBrowserSettingsFlow(params);
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const getSettingFlow = async (params: GetSettingsFlowRequest): Promise<SettingsFlow> => {
    const flowId = params.id;
    if (!flowId) {
      throw new Error('Flow ID is required');
    }

    try {
      const flow = await myOry.getSettingsFlow({ ...params, id: flowId });
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  const updateSettingFlow = async (params: UpdateSettingsFlowRequest): Promise<SettingsFlow> => {
    const flowId = params.flow;
    if (!flowId) {
      throw new Error('Flow ID is required');
    }

    try {
      const flow = await myOry.updateSettingsFlow({ ...params, flow: flowId });
      currentFlow.value = flow;
      return flow;
    } catch (error) {
      return await handleApiError(error);
    }
  };

  return {
    session,
    sessionToJWT,
    currentFlow,
    checkSession,
    createRegistrationFlow,
    updateRegistrationFlow,
    getRegistrationFlow,
    createLogoutFlow,
    updateLogoutFlow,
    getLoginFlow,
    createLoginFlow,
    updateLoginFlow,
    createVerificationFlow,
    getVerificationFlow,
    updateVerificationFlow,
    createBrowserRecoveryFlow,
    getRecoveryFlow,
    updateRecoveryFlow,
    createBrowserSettingFlow,
    getSettingFlow,
    updateSettingFlow,
  };
}

export async function isOryReady(): Promise<boolean> {
  try {
    await myOry.toSession();
    return true;
  } catch (error) {
    const status = (error as { response?: Response })?.response?.status;
    if (status === 401 || status === 403) {
      return true;
    }
    return false;
  }
}

export interface OryGenericError {
  error: {
    id?: string;
    code?: number;
    status?: string;
    reason?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
}

export type OryErrorData = KratosFlow | OryGenericError;

export class OryError extends Error {
  status: number;
  data: OryErrorData;

  constructor(status: number, data: OryErrorData, message: string) {
    super(message);
    this.name = 'OryError';
    this.status = status;
    this.data = data;
  }
}

export class OryAal2RequiredError extends Error {
  constructor() {
    super('Ory session requires AAL2');
    this.name = 'OryAal2RequiredError';
  }
}

export async function handleApiError(error: any): Promise<never> {
  if (error?.response) {
    const errorData: OryErrorData = await error.response.json();
    throw new OryError(error.response.status, errorData, error.message);
  }

  throw error;
}
